import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Select, MenuItem, Button,
  Table, TableBody, TableCell, TableHead, TableRow,
  Chip, CircularProgress, FormControl, InputLabel, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  ToggleButtonGroup, ToggleButton, Alert, Snackbar,
} from '@mui/material';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import axios from 'axios';
import t from '../i18n';
import { Page, Card } from '../ui';

const STATUS = {
  ACTIVE:   { color: 'success', label: 'Активна' },
  PAUSED:   { color: 'warning', label: 'На паузе' },
  ARCHIVED: { color: 'default', label: 'В архиве' },
  DELETED:  { color: 'error',   label: 'Удалена' },
};

export default function CampaignsPage() {
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState([]);        // выбранные id кампаний
  const [busy, setBusy] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [bMode, setBMode] = useState('increase');    // set | increase | decrease
  const [bUnit, setBUnit] = useState('percent');     // percent | amount
  const [bValue, setBValue] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => {
    axios.get('/accounts').then(r => setAccounts(r.data)).catch(() => {});
  }, []);

  const load = () => {
    if (!selected) return;
    setLoading(true); setChecked([]);
    axios.get(`/campaigns/${selected}`).then(r => setCampaigns(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  const actionable = campaigns.filter(c => c.status === 'ACTIVE' || c.status === 'PAUSED');
  const allChecked = actionable.length > 0 && checked.length === actionable.length;
  const toggleAll = () => setChecked(allChecked ? [] : actionable.map(c => c.id));
  const toggle = (id) => setChecked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const run = async (body, okMsg) => {
    setBusy(true);
    try {
      const r = await axios.post('/campaigns/bulk-action', { account_id: selected, ids: checked, ...body });
      const failed = (r.data.results || []).filter(x => !x.ok);
      setToast(failed.length ? `${okMsg}: ${r.data.ok}/${r.data.total}. Ошибок: ${failed.length} (${failed[0].error})` : `${okMsg}: готово ${r.data.ok}/${r.data.total}`);
      setChecked([]); load();
    } catch (e) {
      setToast((e.response && e.response.data && e.response.data.detail) || 'Не удалось выполнить');
    } finally { setBusy(false); setBudgetOpen(false); }
  };

  const applyBudget = () => run(
    { action: 'budget', budget_mode: bMode, budget_value: Number(bValue) || 0, budget_percent: bMode !== 'set' && bUnit === 'percent' },
    'Бюджет'
  );
  const del = () => {
    if (!window.confirm(`Удалить кампаний: ${checked.length}? Это необратимо в Facebook.`)) return;
    run({ action: 'delete' }, 'Удаление');
  };

  const headSx = { fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.06em', textTransform: 'uppercase' };

  return (
    <Page title={t.campaignsTitle} subtitle={t.campaignsSubtitle}>
      <Card sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>{t.selectAccountPlaceholder}</InputLabel>
            <Select value={selected} label={t.selectAccountPlaceholder} onChange={e => { setSelected(e.target.value); setCampaigns([]); setChecked([]); }}>
              {accounts.map(a => (
                <MenuItem key={a.id} value={a.id}>{a.name || a.id}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button onClick={load} disabled={!selected || loading} variant="contained" sx={{ px: 3 }}>
            {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t.loadCampaigns}
          </Button>
        </Box>
      </Card>

      <Card sx={{ p: 0, overflow: 'hidden' }}>
        <Table sx={{ '& td, & th': { borderColor: 'divider' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell padding="checkbox">
                <Checkbox size="small" checked={allChecked} indeterminate={checked.length > 0 && !allChecked}
                  onChange={toggleAll} disabled={actionable.length === 0} />
              </TableCell>
              <TableCell sx={headSx}>{t.campaign}</TableCell>
              <TableCell sx={headSx}>{t.status}</TableCell>
              <TableCell sx={headSx}>{t.objective}</TableCell>
              <TableCell sx={headSx}>{t.budget}</TableCell>
              <TableCell sx={headSx}>{t.spend}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 7, textAlign: 'center', border: 'none' }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
                    <CampaignRoundedIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>{t.noCampaigns}</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>{t.noCampaignsDesc}</Typography>
                </TableCell>
              </TableRow>
            ) : campaigns.map(c => {
              const sc = STATUS[c.status] || STATUS.ARCHIVED;
              const selectable = c.status === 'ACTIVE' || c.status === 'PAUSED';
              return (
                <TableRow key={c.id} hover selected={checked.includes(c.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={checked.includes(c.id)} disabled={!selectable} onChange={() => toggle(c.id)} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>{c.name}</TableCell>
                  <TableCell>
                    <Chip label={sc.label} size="small" color={sc.color} variant="outlined" sx={{ height: 22, fontSize: 11, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{c.objective || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 13, color: 'text.primary' }}>{c.budget ? `$${c.budget}` : '—'}</TableCell>
                  <TableCell sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>{c.spend ? `$${parseFloat(c.spend).toFixed(2)}` : '$0.00'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Плавающая панель массовых действий */}
      {checked.length > 0 && (
        <Box sx={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1200,
          display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25,
          bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
          borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
        }}>
          <Chip label={`Выбрано: ${checked.length}`} size="small" color="primary" sx={{ fontWeight: 700 }} />
          <Button size="small" startIcon={<PlayArrowRoundedIcon />} disabled={busy} onClick={() => run({ action: 'activate' }, 'Активация')}>Старт</Button>
          <Button size="small" startIcon={<PauseRoundedIcon />} disabled={busy} onClick={() => run({ action: 'pause' }, 'Пауза')}>Пауза</Button>
          <Button size="small" startIcon={<PaidRoundedIcon />} disabled={busy} onClick={() => { setBValue(''); setBudgetOpen(true); }}>Бюджет</Button>
          <Button size="small" color="error" startIcon={<DeleteOutlineRoundedIcon />} disabled={busy} onClick={del}>Удалить</Button>
          <Button size="small" onClick={() => setChecked([])} sx={{ color: 'text.secondary' }}>Снять</Button>
          {busy && <CircularProgress size={18} sx={{ ml: 0.5 }} />}
        </Box>
      )}

      {/* Диалог изменения бюджета */}
      <Dialog open={budgetOpen} onClose={() => setBudgetOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Бюджет для {checked.length} кампаний</DialogTitle>
        <DialogContent>
          <ToggleButtonGroup size="small" exclusive fullWidth value={bMode} onChange={(e, v) => v && setBMode(v)} sx={{ mb: 2 }}>
            <ToggleButton value="increase" sx={{ textTransform: 'none' }}>Увеличить</ToggleButton>
            <ToggleButton value="decrease" sx={{ textTransform: 'none' }}>Уменьшить</ToggleButton>
            <ToggleButton value="set" sx={{ textTransform: 'none' }}>Установить</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField autoFocus size="small" type="number" label={bMode === 'set' ? 'Дневной бюджет ($)' : (bUnit === 'percent' ? 'Величина (%)' : 'Величина ($)')}
              value={bValue} onChange={(e) => setBValue(e.target.value)} sx={{ flex: 1 }} />
            {bMode !== 'set' && (
              <ToggleButtonGroup size="small" exclusive value={bUnit} onChange={(e, v) => v && setBUnit(v)}>
                <ToggleButton value="percent" sx={{ px: 1.5 }}>%</ToggleButton>
                <ToggleButton value="amount" sx={{ px: 1.5 }}>$</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
          <Alert severity="info" sx={{ mt: 2, borderRadius: '8px', fontSize: 12 }}>
            Меняется бюджет уровня кампании (CBO). Для ABO-кампаний бюджет живёт на группах — Facebook вернёт ошибку по таким.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBudgetOpen(false)} sx={{ color: 'text.secondary' }}>Отмена</Button>
          <Button variant="contained" disabled={busy || !bValue} onClick={applyBudget}>Применить</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast('')}
        message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} />
    </Page>
  );
}
